/**
 * The deal's broker, when it is still waiting for a person to confirm it.
 *
 * Intake proposes a broker and a brokerage from the email; by governance
 * (rule Q1) nothing is created or linked in the broker directory until a
 * person confirms. The confirmation lived only on the Intake screen, so from
 * the deal the broker simply looked unlinked — 35 of 35 intake deals, 43
 * confirmations waiting, while the register described the link as automatic.
 * This puts the proposal and its Confirm on the deal's Parties tab, and says
 * how many other records are waiting for the same step.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import BrokerResolutionCard from '@/components/intake/BrokerResolutionCard'
import { ingestionService, type BrokerBatchResult, type BrokerResolution } from '@/services/ingestion'

const UNRESOLVED = new Set(['candidates', 'conflict', 'blocked'])

function brokerAwaitingConfirmation(resolution: BrokerResolution | null | undefined): boolean {
  if (!resolution) return false
  return UNRESOLVED.has(resolution.firmStatus) || UNRESOLVED.has(resolution.contactStatus)
}

/**
 * What confirming a record would link, in words: "Ben Ortiz · bortiz@… · Desert Capital Group".
 *
 * A contact already in the CRM carries the brokerage that holds them: "John
 * Smith (JLL)". A report stored before review finding 11's fix proposed JLL's
 * John Smith on a Cushman & Wakefield record, and the list read just "John
 * Smith" beside the "Confirm selected" that would have linked him.
 */
function proposalOf(resolution: BrokerResolution | null | undefined): string | null {
  const contact = resolution?.candidates?.contact?.[0]
  const firm = resolution?.candidates?.firm?.[0]
  const person = contact?.name && contact.id && contact.firm ? `${contact.name} (${contact.firm})` : contact?.name
  const parts = [person, contact?.email, firm?.name].filter(Boolean)
  return parts.length ? parts.join(' · ') : null
}

interface Props {
  loanId: string
  ingestionId: string
  resolution: BrokerResolution | null | undefined
  brokerId?: string | null
  brokerFirmId?: string | null
  /** The dossier is locked: the proposal is shown, but not confirmable here. */
  locked?: boolean
}

export function BrokerLinkPanel({ loanId, ingestionId, resolution, brokerId, brokerFirmId, locked = false }: Props) {
  const queryClient = useQueryClient()
  const waiting = brokerAwaitingConfirmation(resolution) && !brokerId
  const [selected, setSelected] = useState<string[]>([])
  const [outcome, setOutcome] = useState<BrokerBatchResult | null>(null)
  const refresh = () =>
    Promise.all(
      [
        ['ingestion-record', ingestionId],
        ['deal-parties', loanId],
        ['loan', loanId],
        ['loans'],
        ['broker-review'],
        ['broker-firms'],
        ['broker-contacts'],
        ['crm', 'contacts'],
      ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    )
  const batch = useMutation({
    mutationFn: (ids: string[]) => ingestionService.confirmBrokerBatch(ids),
    onSuccess: async (result) => {
      setOutcome(result)
      setSelected([])
      await refresh()
    },
  })
  const queue = useQuery({
    queryKey: ['broker-review'],
    queryFn: ingestionService.getBrokerReview,
    enabled: waiting,
    staleTime: 60_000,
    retry: false,
  })
  if (!waiting) return null

  const others = (queue.data?.items ?? []).filter((item) => item.ingestionId !== ingestionId)

  return (
    <section className="pi-panel" aria-label="Broker confirmation">
      <header className="pi-panel-heading">
        <h3>Broker not linked to the directory</h3>
      </header>
      <p className="text-[11px] text-stone-400">
        Intake proposed this deal&apos;s broker and brokerage from the email. By rule, a person confirms before either
        record is created or linked — until then this deal is missing from the broker&apos;s book, scorecard and
        &ldquo;every deal this firm sent&rdquo;.
      </p>
      {others.length > 0 ? (
        <details className="mt-1 text-[11px] text-stone-400">
          <summary className="cursor-pointer text-amber-200/90">
            {others.length} other intake record{others.length === 1 ? ' is' : 's are'} waiting for a broker
            confirmation
          </summary>
          <ul className="mt-1 space-y-0.5">
            {others.slice(0, 12).map((item) => {
              const label = item.propertyName || item.ingestionId
              const proposal = proposalOf(item.resolution)
              return (
                <li key={item.ingestionId} className="flex items-baseline gap-1.5">
                  <input
                    type="checkbox"
                    aria-label={`Select ${label}`}
                    checked={selected.includes(item.ingestionId)}
                    disabled={locked || batch.isPending}
                    onChange={(event) =>
                      setSelected((current) =>
                        event.target.checked
                          ? [...current, item.ingestionId]
                          : current.filter((id) => id !== item.ingestionId),
                      )
                    }
                  />
                  <span>
                    <Link
                      to={`/intake?focus=${encodeURIComponent(item.ingestionId)}`}
                      className="underline decoration-dotted underline-offset-2 hover:text-amber-200"
                    >
                      {label}
                    </Link>
                    {proposal ? (
                      <span className="text-stone-500"> · proposed {proposal}</span>
                    ) : item.brokerName ? (
                      <span className="text-stone-500"> · {item.brokerName}</span>
                    ) : null}
                  </span>
                </li>
              )
            })}
          </ul>
          <div className="mt-1.5 flex items-center gap-2">
            <button
              type="button"
              className="btn-secondary text-[11px]"
              disabled={locked || batch.isPending || selected.length === 0}
              title={locked ? 'Unlock the dossier to make changes' : undefined}
              onClick={() => batch.mutate(selected)}
            >
              Confirm selected ({selected.length})
            </button>
            <span className="text-[10px] text-stone-500">
              Confirms exactly what each selected record proposed; a record that needs a choice stays waiting.
            </span>
          </div>
          {batch.isError ? (
            <p role="alert" className="mt-1 text-[11px] text-red-300">
              The confirmation did not go through. Nothing was changed.
            </p>
          ) : null}
          {outcome ? (
            <div role="status" className="mt-1 text-[11px] text-stone-300">
              <p>
                Confirmed {outcome.confirmed.length}
                {outcome.confirmed.some((c) => c.alsoLinked.length)
                  ? `, and linked ${outcome.confirmed.reduce((n, c) => n + c.alsoLinked.length, 0)} more from the same address`
                  : ''}
                .
              </p>
              {outcome.failed.length ? (
                <ul className="mt-0.5 space-y-0.5 text-amber-200/90">
                  {outcome.failed.map((f) => (
                    <li key={f.ingestionId}>
                      Not confirmed — {others.find((o) => o.ingestionId === f.ingestionId)?.propertyName || f.ingestionId}:{' '}
                      {f.reason}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
          {others.length > 12 ? (
            <Link to="/intake" className="mt-1 inline-block underline decoration-dotted underline-offset-2">
              All {others.length} in Intake
            </Link>
          ) : null}
        </details>
      ) : null}
      <div className="mt-2">
        <BrokerResolutionCard
          key={ingestionId}
          ingestionId={ingestionId}
          resolution={resolution}
          brokerId={brokerId ?? undefined}
          brokerFirmId={brokerFirmId ?? undefined}
          lockedReason={locked ? 'Unlock the dossier to make changes' : undefined}
          onResolved={async () => {
            await refresh()
          }}
        />
      </div>
    </section>
  )
}
